/**
 * Utility functions for file handling
 */

/**
 * Strip query-string parameters from a URL (handles presigned S3 URLs).
 * For non-URL strings (plain filenames), returns the input unchanged.
 * @param {string} url
 * @returns {string}
 */
export const stripQueryString = (url) => {
  if (!url) return url;
  const qIdx = url.indexOf('?');
  return qIdx !== -1 ? url.substring(0, qIdx) : url;
};

/**
 * Extract clean filename by removing UUID suffix added during upload
 * @param {string} filename - The filename that may contain UUID
 * @returns {string} - Clean filename without UUID
 */
export const extractCleanFilename = (filename) => {
  if (!filename) return '';
  
  // Remove file extension first
  const lastDotIndex = filename.lastIndexOf('.');
  const nameWithoutExt = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';
  
  // Pattern to match UUID suffix: _8characters (hex)
  // Example: "document_a1b2c3d4.pdf" -> "document.pdf"
  const uuidPattern = /_[a-f0-9]{8}$/i;
  
  if (uuidPattern.test(nameWithoutExt)) {
    const cleanName = nameWithoutExt.replace(uuidPattern, '');
    return cleanName + extension;
  }
  
  return filename;
};

/**
 * Create display name from filename by removing only UUID and extension
 * @param {string} filename - The filename to process
 * @returns {string} - Display name with UUID removed but original characters preserved
 */
export const createOriginalDisplayName = (filename) => {
  if (!filename) return '';
  
  // First remove UUID suffix
  const cleanFilename = extractCleanFilename(filename);
  
  // Remove extension for display name
  const lastDotIndex = cleanFilename.lastIndexOf('.');
  const nameWithoutExt = lastDotIndex > 0 ? cleanFilename.substring(0, lastDotIndex) : cleanFilename;
  
  // Return original name without UUID and extension, preserving all original characters
  return nameWithoutExt;
};

/**
 * Get display name for a file, prioritizing user-defined names
 * @param {Object} file - File object with file_name, original_filename, file_url properties
 * @returns {string} - Display name for the file
 */
export const getFileDisplayName = (file) => {
  if (!file) return 'Unnamed File';
  
  // If user has set a custom file_name, use that
  if (file.file_name && file.file_name.trim() !== '') {
    return file.file_name;
  }
  
  // Fallback to original filename
  if (file.original_filename && file.original_filename.trim() !== '') {
    return file.original_filename;
  }
  
  // Fallback to filename from URL (strip presigned query params first)
  if (file.file_url) {
    const filename = stripQueryString(file.file_url).split('/').pop();
    if (filename) {
      return filename;
    }
  }
  // Fallback to basename from s3 key when available
  if (file.file_s3_key) {
    const filename = file.file_s3_key.split('/').pop();
    if (filename) {
      return filename;
    }
  }
  
  return 'Unnamed File';
};

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < sizes.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${sizes[unitIndex]}`;
};

/**
 * Get file extension from filename or URL.
 * Safely strips presigned S3 query params before parsing.
 * @param {string} filename - Filename or URL (plain or presigned)
 * @returns {string} - File extension in lowercase
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';

  // Strip query string so presigned S3 URLs don't pollute the extension
  const clean = stripQueryString(filename);
  const name = clean.split('/').pop();
  const lastDotIndex = name.lastIndexOf('.');

  if (lastDotIndex > 0 && lastDotIndex < name.length - 1) {
    return name.substring(lastDotIndex + 1).toLowerCase();
  }

  return '';
};

/**
 * Check if a file type supports preview
 * @param {string} extension - File extension
 * @returns {boolean} - Whether the file type supports preview
 */
export const supportsPreview = (extension) => {
  const previewableTypes = [
    // Images
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
    // Documents
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    // Text
    'txt', 'md', 'json', 'xml', 'csv', 'log',
    // Code
    'js', 'jsx', 'ts', 'tsx', 'html', 'css', 'py', 'java', 'cpp', 'c', 'php', 'rb', 'go', 'rs', 'sh',
    // Media
    'mp3', 'wav', 'ogg', 'm4a', 'aac', 'mp4', 'webm', 'avi', 'mov'
  ];
  
  return previewableTypes.includes(extension.toLowerCase());
};
